import { PersonalDetailsSection } from '../employee-edit/sections/personal-details';
import { useSendPersonalDetailsMutation } from './hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { PersonalDetailsDto } from 'api/generated';
import { ScrollTopComponent } from 'components/layout/initializers/scroll-top-component';
import { Loading } from 'components/loading';
import { useQueryResponse } from 'components/table/contexts/query-response';
import { Employee } from 'features/employees/api/models/employee';
import { UpdateEmployeeInfoInput } from 'features/employees/api/repositories/use-emplayee-info-update.mutation';
import { UpdateEmployeePersonalDetailsInput } from 'features/employees/api/repositories/use-employee-personal-details-update.mutation';
import { employeeQueryResponseContext } from 'features/employees/contexts/employeeQueryResponse';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

type Props = {
  uid: string;
  isEmployeeLoading: boolean;
  personalDetails: PersonalDetailsDto;
};

const personalInfoDefaultValues: UpdateEmployeeInfoInput = {
  input: {
    id: 0,
    formData: {
      Uid: '',
      Name: '',
      BranchId: 0,
      JobTitle: '',
      jobTitleType: 0,
      Email: '',
      Role: 0,
      DepartmentId: 0,
      WorkingHoursPerWeek: 40
    }
  }
};

const personalInfoSchema = yup.object().shape({
  input: yup.object({
    id: yup.string().required('Id is required'),
    formData: yup.object({
      Name: yup.string().required('Name is required'),
      BranchId: yup.number().moreThan(0, 'Branch is required'),
      JobTitle: yup.string().required('Job title is required'),
      Email: yup.string().email('Must be a valid email').max(255).required('Email is required'),
      Role: yup.number().moreThan(0, 'Role is required'),
      DepartmentId: yup.number().moreThan(0, 'Department is required')
    })
  })
});

const personalDetailsSchema = yup.object().shape({
  input: yup.object({
    id: yup.number().required('Id is required'),
    requestBody: yup.object({
      maritalStatus: yup.string().required('Marital status is required'),
      penaltiesAndDisqualifications: yup.string().max(256, 'The value cannot be greater than 256')
    })
  })
});

export const PersonalDataForm: FC<Props> = ({ uid, personalDetails, isEmployeeLoading }) => {
  const { refetch } = useQueryResponse(employeeQueryResponseContext);

  const { mutate: updateEmployee } = useSendPersonalDetailsMutation();

  const personalDetailsValues: any = {
    input: {
      requestBody: {
        birthDate: personalDetails.birthDate,
        commencedDate: personalDetails.commencedDate,
        homeAddress: personalDetails.homeAddress,
        homePhoneNumber: personalDetails.homePhoneNumber,
        gender: personalDetails.gender,
        maritalStatus: personalDetails.maritalStatus || 0,
        mobilePhoneNumber: personalDetails.mobilePhoneNumber,
        nationalInsNo: personalDetails.nationalInsNo,
        nationality: personalDetails.nationality,
        nextOfKinOrRelationship: personalDetails.nextOfKinOrRelationship,
        personalEmail: personalDetails.personalEmail,
        nextOfKinsDayTimeNo: personalDetails.nextOfKinsDayTimeNo,
        patronymic: personalDetails.patronymic,
        surname: personalDetails.surname,
        doctorName: personalDetails.doctorName,
        doctorTelNo: personalDetails.doctorTelNo,
        doctorAddress: personalDetails.doctorAddress,
        workType: personalDetails.workType,
        bankName: personalDetails.bankName,
        bankSortCode: personalDetails.bankSortCode,
        bankAccountNo: personalDetails.bankAccountNo,
        bankPayeeName: personalDetails.bankPayeeName,
        userSendFill: personalDetails.userSendFill,
        dateSendFill: personalDetails.dateSendFill,
        postCode: personalDetails.postCode
      }
    }
  };

  const {
    reset: resetPersonalInfo,
    getValues: personalInfoValues,
    formState: { isSubmitting: isPersonalInfoSubmitting }
  } = useForm<UpdateEmployeeInfoInput>({
    defaultValues: personalInfoDefaultValues,
    resolver: yupResolver(personalInfoSchema),
    mode: 'onBlur'
  });

  const {
    register: personalDetailsRegister,
    reset: resetPersonalDetail,
    control: personalDetailsControl,
    getValues: getPersonalDetailsValues,
    setValue: personalDetailsSetValue,
    trigger: personalDetailsTriggerValidation,
    watch: personalDetailsWatch,
    formState: {
      errors: personalDetailsErrors,
      touchedFields: personalDetailsFields,
      isSubmitting: isPersonalDetailsSubmitting,
      isValid: isPersonalDetailsValid
    }
  } = useForm<UpdateEmployeePersonalDetailsInput>({
    defaultValues: personalDetailsValues,
    resolver: yupResolver(personalDetailsSchema),
    mode: 'onBlur'
  });

  const onSubmit = () => {
    const personalInfoInput = personalInfoValues();
    personalInfoInput.input.formData.ImageFile = undefined;
    const personalDetailsInput = getPersonalDetailsValues();
    updateEmployee(
      { uid, requestBody: personalDetailsInput.input.requestBody },
      {
        onSuccess: () => {
          toast.success('Successfully updated');
          cancel(true);
        }
      }
    );
  };

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    } else {
      resetPersonalDetail();
      ScrollTopComponent.goTop();
    }
  };

  const isLoading = isEmployeeLoading;

  const isSubmitting = isPersonalDetailsSubmitting || isPersonalInfoSubmitting;
  const isValid = isPersonalDetailsValid;

  //TODO: Cover with unit tests
  const check = {
    dateSendFill: personalDetails.dateSendFill,
    isConfirmed: personalDetails.isConfirmed
  };
  if (personalDetails && (!check.dateSendFill || check.isConfirmed)) {
    const msg = 'Form cannot be viewed';

    return <div className="alert alert-dark text-center w-100 fw-bolder fs-2">{msg}</div>;
  }

  return (
    <>
      {isLoading && <Loading />}
      <PersonalDetailsSection
        isLoading={false}
        isSubmitting={isPersonalDetailsSubmitting}
        getValues={getPersonalDetailsValues}
        setValue={personalDetailsSetValue}
        trigger={personalDetailsTriggerValidation}
        register={personalDetailsRegister}
        control={personalDetailsControl}
        touchedFields={personalDetailsFields}
        errors={personalDetailsErrors}
        queryResult={personalDetails}
        watch={personalDetailsWatch}
      />
      <div className="card-footer d-flex justify-content-start py-6 px-9">
        <div className="d-flex flex-row justify-content-left">
          <button onClick={onSubmit} className="btn btn-primary me-5">
            {!isSubmitting && 'Save Changes'}
            {isSubmitting && (
              <span className="indicator-progress" style={{ display: 'block' }}>
                Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
          <button onClick={() => cancel()} className="btn btn-light-primary">
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};
